import { FRAME_SIZES } from '@/constants/bike'

// Shared by the interest form and its API route, so both sides validate the same way.

export const SIZE_OPTIONS = [...FRAME_SIZES, 'not-sure'] as const

export type SizeOption = typeof SIZE_OPTIONS[number]

export type InterestField = 'name' | 'email' | 'phone' | 'size' | 'message'

export const INTEREST_FIELDS: Array<InterestField> = ['name', 'email', 'phone', 'size', 'message']

export type InterestInput = Record<InterestField, string>

export type InterestErrors = Partial<Record<InterestField, true>>

export const MAX_NAME_LENGTH = 120

export const MAX_EMAIL_LENGTH = 254

export const MAX_PHONE_LENGTH = 32

export const MAX_MESSAGE_LENGTH = 1000

const EMAIL_PATTERN = /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[^\s@<>"',;]{2,}$/

const PHONE_PATTERN = /^\+?[\d\s().-]{6,31}$/

export const asText = (value: unknown): string => (typeof value === 'string' ? value : '')

export const singleLine = (value: string): string => value.replace(/\s+/g, ' ').trim()

export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value) && value.length <= MAX_EMAIL_LENGTH

export const isValidPhone = (value: string): boolean => PHONE_PATTERN.test(value) && value.length <= MAX_PHONE_LENGTH

export const normalizeInterest = (raw: Partial<Record<InterestField, unknown>>): InterestInput => ({
    name: singleLine(asText(raw.name)),
    email: asText(raw.email).trim().toLowerCase(),
    phone: singleLine(asText(raw.phone)),
    size: asText(raw.size).trim(),
    message: asText(raw.message).replace(/\r\n/g, '\n').trim()
})

export const validateInterest = (input: InterestInput): InterestErrors => {
    const errors: InterestErrors = {}

    if (!input.name || input.name.length > MAX_NAME_LENGTH) errors.name = true
    if (!isValidEmail(input.email)) errors.email = true
    if (input.phone && !isValidPhone(input.phone)) errors.phone = true
    if (!SIZE_OPTIONS.includes(input.size as SizeOption)) errors.size = true
    if (input.message.length > MAX_MESSAGE_LENGTH) errors.message = true

    return errors
}

export const isInterestErrors = (value: unknown): value is InterestErrors => {
    if (!value || typeof value !== 'object') return false

    return Object.keys(value).every((key) => INTEREST_FIELDS.includes(key as InterestField))
}
